
import * as XLSX from 'xlsx';
import { Farm } from '@/types/database.types';
import { validateFarmRow, normalizeRowFields } from './validation/farmValidation';
import { parseExcelDate } from './utils/dateUtils';

/**
 * Parse farm Excel file
 * @param file Excel file to parse
 * @returns Promise with parsed data and errors
 */
export const parseFarmExcelFile = async (file: File) => {
  const errors: string[] = [];
  const farms: Partial<Farm>[] = [];
  
  try {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data);
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    const jsonData = XLSX.utils.sheet_to_json(worksheet);
    
    if (jsonData.length === 0) {
      errors.push('The Excel file appears to be empty');
      return { data: farms, errors };
    }
    
    jsonData.forEach((row: any, index: number) => {
      try {
        const mappedRow = normalizeRowFields(row);
        const rowErrors = validateFarmRow(mappedRow, index);
        
        if (rowErrors.length > 0) {
          errors.push(...rowErrors);
          return;
        }

        // Process dates
        if (mappedRow.harvest_start_date) {
          const parsedStartDate = parseExcelDate(mappedRow.harvest_start_date);
          if (parsedStartDate) {
            mappedRow.harvest_start_date = parsedStartDate;
          } else {
            console.warn(`Row ${index + 2}: Could not parse harvest start date: ${mappedRow.harvest_start_date}`);
            errors.push(`Row ${index + 2}: Could not parse 'Harvest Start Date' or 'Starting Date'. Please check the format.`);
          }
        }
        
        if (mappedRow.harvest_end_date) {
          const parsedEndDate = parseExcelDate(mappedRow.harvest_end_date);
          if (parsedEndDate) {
            mappedRow.harvest_end_date = parsedEndDate;
          } else {
            console.warn(`Row ${index + 2}: Could not parse harvest end date: ${mappedRow.harvest_end_date}`);
            errors.push(`Row ${index + 2}: Could not parse 'Harvest End Date' or 'Ending Date'. Please check the format.`);
          }
        }

        // Process estimated harvest quantity
        let estimatedHarvestKg = null;
        if (mappedRow.estimated_harvest_kg !== undefined && mappedRow.estimated_harvest_kg !== null) {
          estimatedHarvestKg = Number(mappedRow.estimated_harvest_kg);
          if (isNaN(estimatedHarvestKg)) {
            console.warn(`Row ${index + 2}: Invalid estimated harvest quantity: ${mappedRow.estimated_harvest_kg}`);
            errors.push(`Row ${index + 2}: Invalid 'Estimated Harvest (kg)'. Please enter a valid number.`);
            estimatedHarvestKg = null;
          }
        }

        const farm: Partial<Farm> = {
          region: mappedRow.region,
          land_size: Number(mappedRow.farm_size),
          irrigation_type: mappedRow.irrigation_type || null,
          latitude: mappedRow.latitude ? Number(mappedRow.latitude) : null,
          longitude: mappedRow.longitude ? Number(mappedRow.longitude) : null,
          farmer_name: mappedRow.farmer_name,
          phone_number: mappedRow.phone_number,
          union_name: mappedRow.union_name || null,
          has_contract: mappedRow.has_contract === 'Yes' || mappedRow.has_contract === true,
          has_phone_access: mappedRow.has_phone_access === 'Yes' || mappedRow.has_phone_access === true,
          officer: mappedRow.officer || null,
          product_name: mappedRow.product_name,
          distance_from_addis: mappedRow.distance_from_addis ? Number(mappedRow.distance_from_addis) : 0,
          city: mappedRow.city || null,
          area: mappedRow.area || null,
          harvest_start_date: mappedRow.harvest_start_date || null,
          harvest_end_date: mappedRow.harvest_end_date || null,
          kebelle: mappedRow.kebelle || null,
          estimated_harvest_kg: estimatedHarvestKg
        };
        
        console.log(`Row ${index + 2}: Estimated harvest quantity: ${farm.estimated_harvest_kg}`);
        
        farms.push(farm);
        console.log(`Successfully processed row ${index + 2}`);
      } catch (error) {
        console.error(`Error processing row ${index + 2}:`, error);
        errors.push(`Row ${index + 2}: Error processing row - ${(error as Error).message}`);
      }
    });
    
    if (farms.length === 0) {
      errors.push('No valid entries found in the Excel file. Please check that all required fields are filled in.');
    } else {
      console.log(`Successfully processed ${farms.length} farm entries`);
    }
    
  } catch (error) {
    console.error('Failed to parse Excel file:', error);
    errors.push(`Failed to parse Excel file: ${(error as Error).message}`);
  }
  
  return { data: farms, errors };
};
